import React, { RefObject, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Tooltip } from 'bootstrap';
import { KTIcon } from '../../../../../_theme/helpers';

type Props = {
  text: string;
  type?: 'info' | 'warning' | 'danger' | 'success' | 'primary';
  clear?: boolean;
  extraClass?: string;
  typeTooltip?: 'icon' | 'default';
  Wrapper?: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  trigger?: 'hover' | 'click' | 'focus';
  theme?: 'light' | 'dark';
};

const ThemeTooltip: React.FC<Props> = ({
  text,
  type = 'primary',
  clear = true,
  extraClass = '',
  typeTooltip = 'icon',
  Wrapper = null,
  placement = 'bottom',
  trigger = 'hover',
  theme = 'dark',
}) => {
  const tooltipRef = useRef<HTMLElement | null>(null);
  const tooltipInstance = useRef<Tooltip | null>(null);

  useEffect(() => {
    const element = tooltipRef.current;

    if (!element) return;

    // destroy old instance
    Tooltip.getInstance(element)?.dispose();

    // update title attribute manually
    element.setAttribute('data-bs-original-title', text);
    element.setAttribute('title', text);


    // create fresh instance
    tooltipInstance.current = new Tooltip(element, {
      html: true,
      sanitize: true,
      customClass: clsx(
        'custom-tooltip',
        theme === 'dark' && 'custom-tooltip-dark'
      ),
    });

    return () => {
      try {
        tooltipInstance.current?.hide();
        tooltipInstance.current?.dispose();
        tooltipInstance.current = null;
      } catch (e) {
        console.warn(e);
      }
    };
  }, [text]);

  if (typeTooltip === 'icon') {
    return (
      <button
        type='button'
        className={`btn btn-sm ${clear ? 'btn-clear' : `btn-light btn-light-${type}`
          } p-0 pb-1 ${extraClass}`}
        ref={tooltipRef as RefObject<HTMLButtonElement>}
        data-bs-toggle='tooltip'
        data-bs-placement={placement}
        data-bs-trigger={trigger}
        title={text}
        aria-label='Information'
      >
        <KTIcon
          iconName='information-2'
          className={`fs-3 pe-0 ${clear ? `text-${type}` : ''}`}
        />
      </button>
    );
  }

  return (
    <div
      ref={tooltipRef as RefObject<HTMLDivElement>}
      data-bs-toggle='tooltip'
      data-bs-placement={placement}
      data-bs-html='true'
      title={text}
      aria-label='Information'
    >
      {Wrapper}
    </div>
  );
};

export default ThemeTooltip;